import React, { useState } from 'react';
import type { Park } from '../types';
import { communityService } from '../services/communityService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Calendar, Clock, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/PetParksContext';

interface CreateMeetupModalProps {
  park: Park;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateMeetupModal: React.FC<CreateMeetupModalProps> = ({
  park,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useApp();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [maxParticipants, setMaxParticipants] = useState('10');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!title.trim()) {
      toast.error(t('meetupEnterTitle'));
      return;
    }

    if (!date || !time) {
      toast.error(t('meetupSelectDateTime'));
      return;
    }

    const meetupDateTime = new Date(`${date}T${time}`);
    if (meetupDateTime < new Date()) {
      toast.error(t('meetupTimeFuture'));
      return;
    }

    const durationNum = parseInt(duration);
    const maxNum = parseInt(maxParticipants);

    if (durationNum < 30 || durationNum > 480) {
      toast.error(t('meetupDurationRange'));
      return;
    }

    if (maxNum < 2 || maxNum > 50) {
      toast.error(t('meetupParticipantsRange'));
      return;
    }

    setIsSubmitting(true);

    try {
      await communityService.createMeetup({
        park_id: park.id,
        park_name: park.name,
        organizer_id: 'current-user-id', // TODO: Get from auth
        organizer_name: 'Current User', // TODO: Get from auth
        title,
        description,
        date,
        time,
        duration_minutes: durationNum,
        max_participants: maxNum,
      });

      toast.success(t('meetupCreatedSuccess'));
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast.error(t('meetupCreateFailed'));
      console.error('Meetup creation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setDate('');
    setTime('');
    setDuration('60');
    setMaxParticipants('10');
    onClose();
  };

  const minDate = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('organizeMeetup')}</DialogTitle>
          <DialogDescription>
            {t('createMeetupAtPark', { parkName: park.name })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t('meetupTitleRequired')}</Label>
            <Input
              id="title"
              placeholder={t('meetupTitlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('description')}</Label>
            <Textarea
              id="description"
              placeholder={t('meetupDescriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={500}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/500 characters
            </p>
          </div>

          {/* Date and Time */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="date">{t('dateRequired')}</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  min={minDate}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="time">{t('timeRequired')}</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
          </div>

          {/* Duration and Max Participants */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="duration">{t('meetupDurationMinutes')}</Label>
              <Input
                id="duration"
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="30"
                max="480"
                step="15"
              />
              <p className="text-xs text-muted-foreground">
                {t('meetupDurationHint')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max-participants">
                {t('meetupMaxParticipants')}
              </Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="max-participants"
                  type="number"
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(e.target.value)}
                  min="2"
                  max="50"
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('meetupParticipantsHint')}
              </p>
            </div>
          </div>

          {/* Info */}
          <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <span className="font-medium">
                {t('meetupOrganizerTipsTitle')}
              </span>{' '}
              {t('meetupOrganizerTipsBody')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 cursor-pointer"
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1 cursor-pointer"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('creating')}
                </>
              ) : (
                t('createMeetup')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
